import Tooltip from '@codegouvfr/react-dsfr/Tooltip';
import { useTextes } from '@common/config';
import { Z_FILE } from '@common/schemas';
import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FaCircleInfo } from 'react-icons/fa6';
import { ImAttachment } from 'react-icons/im';
import { MaybeAsync } from 'zod/v4/core/util';
import { useCheckFormSchema } from '../form-field/hooks/use-check-form-schema.hook';
import { useFormFieldParams } from '../form-field/hooks/use-form-field-params.hook';
import { viewFile } from '../index';
import { FilesUploadList } from './components/files-upload-list/FilesUploadList';
import { SavedFile } from './types/saved-file.type';
import './fileUploadField.css';

export const FileUploadField = ({
                                  name,
                                  typesSelection,
                                  onFileDelete,
                                  maxSize,
                                  extensions,
                                  fileTypes,
                                  onFileChange,
                                  requiredErrorMsg,
                                  title,
                                  subtitle,
                                  fallbackDownloadFnIfUrlIsEmpty,
                                  tooltip,
                                }: FileUploadProps) => {

  useCheckFormSchema({ name });

  const t = useTextes('Commun', 'Formulaire');

  const { setValue: setFormValue, setError: setFormError, clearErrors } = useFormContext();
  const { error, currentValue: currentFile, isRequired } = useFormFieldParams<SavedFile>({ name });

  const inputRef = useRef<HTMLInputElement>(null!);

  const [selectedType, setSelectedType] = useState<string | null>(null);

  useEffect(() => {
    setSelectedType(currentFile?.type ?? typesSelection[0]);
  }, []);

  useEffect(() => {
    if (!selectedType || !currentFile) return;

    emitFile({ ...currentFile, type: selectedType });
  }, [selectedType]);


  const validateFile = (file: File) => {
    if (currentFile) return t['REPR_MES5'];

    if (!selectedType) return 'Veuillez sélectionner le type de votre fichier.';

    const validation = Z_FILE({ requiredMessage: requiredErrorMsg, fileTypes, maxSize, extensions }).safeParse(file);
    if (!validation.success) return validation.error.issues[0].message;
  };

  const saveFile = (file?: File) => {
    if (!file) return;

    clearErrors(name);

    const errorMessage = validateFile(file);
    if (errorMessage) return setFormError(name, { type: 'manual', message: errorMessage });

    const savedFile: SavedFile = {
      url: URL.createObjectURL(file),
      name: file.name,
      type: selectedType!,
      size: file.size,
    };

    emitFile(savedFile);
  };

  const emitFile = (file: SavedFile) => {
    setFormValue(name, file);
    onFileChange?.(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    saveFile(event.dataTransfer.files?.[0]);
  };

  const handleChange = (event) => {
    saveFile(event.target.files?.[0]);
  };

  const handleView = () => {
    clearErrors(name);

    const { url } = currentFile ?? {};

    if (url) viewFile(url);
    else fallbackDownloadFnIfUrlIsEmpty?.(currentFile);
  };

  const handleRemove = () => {
    clearErrors(name);

    if (currentFile.url) URL.revokeObjectURL(currentFile.url);

    onFileDelete?.(currentFile);
    setFormValue(name, null);
  };

  return (
    <div className={'upload_container'}>

      <div className={'upload-type-selector-titles'}>
        {(title) && (
          <div className={'flex items-center gap-1'}>
            <span className={'text-black upload-type-selector-title'}>{title}&nbsp;{(isRequired) && '*'}</span>
            {(tooltip) && (
              <Tooltip title={tooltip} kind="hover">
                <FaCircleInfo className={'color-primary'} />
              </Tooltip>
            )}
          </div>
        )}
        {(subtitle) && (
          <span className={'upload-type-selector-subtitle'}>{subtitle}</span>
        )}
      </div>

      {(typesSelection.length > 1) && (
        <div className={'upload-type-selector'}>

          <div className={'upload-type-selector-items'}>
            {typesSelection.map((fileType) => (
              <div key={fileType} className={'upload-type-selector-item'}>
                <label className={'flex items-center gap-2'}>
                  <input type="radio"
                         name="type"
                         className={'upload-type-selector-radio'}
                         value={fileType}
                         onChange={(ev) => setSelectedType(ev.target.value)}
                         checked={selectedType === fileType} />
                  {fileType}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {error?.message && <span className={'error-message'}>{error?.message}</span>}

      <div className={'drag_container'}
           onDrop={handleDrop}
           onClick={(event) => {
             event.stopPropagation();
             inputRef.current?.click();
           }}
           onDragOver={(event) => event.preventDefault()}>

        <ImAttachment className={'upload_logo'} />

        <label htmlFor="files-input" className={'upload_title'}>Ajouter un fichier</label>

        <span
          className={'upload_desc'}>Taille maximale {Math.round(maxSize / 1024 / 1024)} Mo. Format{extensions.length > 1 ? 's' : ''} supporté{extensions.length > 1 ? 's' : ''} : {extensions.join(', ')}.</span>

        <input type="file"
               id="files-input"
               hidden
               name={name}
               onChange={handleChange}
               accept={extensions.join(',')}
               ref={inputRef} />
      </div>

      {(currentFile) && (
        <FilesUploadList files={[currentFile]} handleRemove={handleRemove} handleView={handleView} />
      )}
    </div>
  );
};

interface FileUploadProps {
  /**
   * Form key name.
   */
  name: string;
  /**
   * Natures de fichier affichées dans le selector
   * Non affiché s'il n'y a qu'un seul élément
   */
  typesSelection: string[];
  /**
   * @example For 10Mo: 10 * 1024 * 1024
   */
  maxSize: number;
  /**
   * @example ['.png', '.jpg', '.pdf']
   */
  extensions: string[];
  /**
   * @example ['application/pdf']
   */
  fileTypes: string[];
  /**
   * Output to get the loaded file.
   */
  onFileChange?: (file: SavedFile) => void;
  /**
   * Output to get the deleted saved file.
   */
  onFileDelete?: (file: SavedFile) => void;
  requiredErrorMsg: string;
  title?: string;
  subtitle?: string;
  /**
   * Utilisé si pas d'URL dans le SavedFile
   */
  fallbackDownloadFnIfUrlIsEmpty?: (file: SavedFile) => MaybeAsync<void>;
  tooltip?: string;
}
